import { useCallback, useMemo } from 'react';
import type { SyntheticEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  SecondaryNavigationActionButton,
  SecondaryNavigationIconButton,
  SecondaryNavigationInfoLabel,
  SecondaryNavigationOverflowContext,
  SecondaryNavigation as SecondaryNavigationPrimitive,
  SecondaryNavigationSelector,
  SecondaryNavigationSkeleton,
  SecondaryNavigationStatus,
  useSecondaryNavigationOverflowMeasurement,
} from 'components/ui';
import type { SecondaryNavigationSkeletonProps } from 'components/ui';
import { useFirmBaseLayoutContext } from 'context/FirmBaseLayoutContext';
import type {
  SecondaryNavigationActionControlConfig,
  SecondaryNavigationTitleAccessoryControlConfig,
} from 'context/FirmBaseLayoutContext';
import { useZoom } from 'context/ZoomContext';
import {
  SecondaryNavigationScrollableActions,
  SecondaryNavigationScrollableActionsGroup,
} from './SecondaryNavigation.styles';

const getPathnameFromNavigationTarget = (to: string) => to.split('?')[0] || to;

const getSecondaryNavigationControlKey = (
  control: SecondaryNavigationActionControlConfig | SecondaryNavigationTitleAccessoryControlConfig,
  index: number
) => control.key ?? `${control.type}-${index}`;

const renderSecondaryNavigationControl = (
  control: SecondaryNavigationActionControlConfig | SecondaryNavigationTitleAccessoryControlConfig,
  index: number
) => {
  const key = getSecondaryNavigationControlKey(control, index);

  switch (control.type) {
    case 'button':
      return (
        <SecondaryNavigationActionButton
          key={key}
          data-testid={control.key}
          disabled={control.disabled}
          endIcon={control.endIcon}
          errorLabel={control.errorLabel}
          isSaving={control.isSaving}
          onClick={control.onClick}
          saveFailed={control.saveFailed}
          savedLabel={control.savedLabel}
          savingLabel={control.savingLabel}
          startIcon={control.startIcon}
          data-tour={control.dataTour}
          variant={control.variant}>
          {control.label}
        </SecondaryNavigationActionButton>
      );
    case 'iconButton':
      return (
        <SecondaryNavigationIconButton
          key={key}
          active={control.active}
          ariaLabel={control.ariaLabel}
          disabled={control.disabled}
          icon={control.icon}
          onClick={control.onClick}
          data-tour={control.dataTour}
          tooltipTitle={control.tooltipTitle}
          variant={control.variant}
        />
      );
    case 'infoLabel':
      return (
        <SecondaryNavigationInfoLabel
          key={key}
          data-tour={control.dataTour}
          label={control.label}
          value={control.value}
          valueTone={control.valueTone}
        />
      );
    case 'selector':
      return (
        <SecondaryNavigationSelector
          key={key}
          disabled={control.disabled}
          endIcon={control.endIcon}
          label={control.label}
          onClick={control.onClick}
          positiveFeedbackDurationMs={control.positiveFeedbackDurationMs}
          positiveFeedbackKey={control.positiveFeedbackKey}
          startIcon={control.startIcon}
          data-tour={control.dataTour}
          value={control.value}
          valueTone={control.valueTone}
          variant={control.variant}
        />
      );
    default:
      return null;
  }
};

const renderSecondaryNavigationControls = (controls: SecondaryNavigationActionControlConfig[] = []) =>
  controls.map(renderSecondaryNavigationControl);

interface FirmBaseSecondaryNavigationProps {
  'data-tour'?: string;
}

export const FirmBaseSecondaryNavigationSkeleton = ({
  ariaLabel = 'Loading secondary navigation',
  itemCount = 4,
  responsiveBehavior = 'inline-scroll',
  ...props
}: SecondaryNavigationSkeletonProps) => (
  <>
    <SecondaryNavigationSkeleton
      {...props}
      ariaLabel={ariaLabel}
      itemCount={0}
      responsiveBehavior={responsiveBehavior}
    />
    {itemCount > 0 && (
      <SecondaryNavigationSkeleton
        {...props}
        ariaLabel={`${ariaLabel} sections`}
        actionCount={0}
        itemCount={itemCount}
        responsiveBehavior={responsiveBehavior}
        showActions={false}
        showStatus={false}
        showTitle={false}
        showTitleAccessory={false}
      />
    )}
  </>
);

const SecondaryNavigation = (rootProps: FirmBaseSecondaryNavigationProps) => {
  const { secondaryNavigation } = useFirmBaseLayoutContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { zoomLevel } = useZoom();
  const { contextValue: overflowContextValue, measureRef: scrollableActionsRef } =
    useSecondaryNavigationOverflowMeasurement(zoomLevel);

  const items = useMemo(() => secondaryNavigation?.items ?? [], [secondaryNavigation?.items]);

  const activeValue = useMemo(() => {
    // Allow pages to override URL-derived matching — required when items represent
    // parent tabs whose URLs are prefixes of the current nested route (e.g. a tab
    // pointing at /intelligence/summaries must remain active under /intelligence/summaries/view-3).
    if (secondaryNavigation?.value !== undefined) {
      return secondaryNavigation.value;
    }

    const currentUrl = `${location.pathname}${location.search}`;
    const activeItem = items.find(item => {
      if (!item.to) {
        return false;
      }

      return (
        item.to === currentUrl ||
        getPathnameFromNavigationTarget(item.to) === location.pathname ||
        (item.activeFor ?? []).includes(location.pathname)
      );
    });

    return activeItem?.value ?? false;
  }, [items, location.pathname, location.search, secondaryNavigation?.value]);

  const handleChange = useCallback(
    (_: SyntheticEvent, value: string) => {
      const selectedItem = items.find(item => item.value === value);

      if (!selectedItem?.to || selectedItem.disabled) {
        return;
      }

      navigate(selectedItem.to);
    },
    [items, navigate]
  );

  const navigationItems = useMemo(
    () =>
      items.map(item => ({
        disabled: item.disabled || !item.to,
        label: item.label,
        value: item.value,
      })),
    [items]
  );

  const actionsNode = useMemo(() => {
    // Scope/metric controls precede status badges; icon buttons stay outside the scrollable area, pinned at the end.
    const actions = secondaryNavigation?.actions ?? [];
    const scopeControls = renderSecondaryNavigationControls(actions.filter(action => action.type !== 'iconButton'));
    const iconButtonControls = renderSecondaryNavigationControls(actions.filter(action => action.type === 'iconButton'));
    const hasScrollableContent =
      scopeControls.length > 0 || secondaryNavigation?.actionsNode || secondaryNavigation?.status;

    const hasContent = hasScrollableContent || iconButtonControls.length > 0;

    if (!hasContent) {
      return undefined;
    }

    return (
      <>
        {hasScrollableContent && (
          <SecondaryNavigationScrollableActions ref={scrollableActionsRef}>
            <SecondaryNavigationOverflowContext.Provider value={overflowContextValue}>
              <SecondaryNavigationScrollableActionsGroup>
                {secondaryNavigation?.actionsNode}
                {scopeControls}
                {secondaryNavigation?.status && (
                  <SecondaryNavigationStatus>{secondaryNavigation.status}</SecondaryNavigationStatus>
                )}
              </SecondaryNavigationScrollableActionsGroup>
            </SecondaryNavigationOverflowContext.Provider>
          </SecondaryNavigationScrollableActions>
        )}
        {iconButtonControls}
      </>
    );
  }, [
    secondaryNavigation?.actions,
    secondaryNavigation?.actionsNode,
    secondaryNavigation?.status,
    overflowContextValue,
    scrollableActionsRef,
  ]);

  const titleAccessory = useMemo(() => {
    const titleAccessoryControl = secondaryNavigation?.titleAccessory
      ? renderSecondaryNavigationControl(secondaryNavigation.titleAccessory, 0)
      : null;

    if (!titleAccessoryControl && !secondaryNavigation?.titleAccessoryNode) {
      return undefined;
    }

    return (
      <>
        {titleAccessoryControl}
        {secondaryNavigation?.titleAccessoryNode}
      </>
    );
  }, [secondaryNavigation?.titleAccessory, secondaryNavigation?.titleAccessoryNode]);

  if (!secondaryNavigation) {
    return null;
  }

  if (secondaryNavigation.isLoading) {
    return (
      <FirmBaseSecondaryNavigationSkeleton
        ariaLabel={secondaryNavigation.ariaLabel}
        zoomLevel={zoomLevel}
        {...secondaryNavigation.skeletonProps}
      />
    );
  }

  return (
    <SecondaryNavigationPrimitive
      actions={actionsNode}
      ariaLabel={secondaryNavigation.ariaLabel}
      items={navigationItems}
      onChange={handleChange}
      responsiveBehavior="inline-scroll"
      title={secondaryNavigation.title}
      titleAccessory={titleAccessory}
      titleLeading={secondaryNavigation.titleLeading}
      titleTooltip={secondaryNavigation.titleTooltip}
      value={activeValue}
      zoomLevel={zoomLevel}
      {...rootProps}
    />
  );
};

export default SecondaryNavigation;
